import { z } from "zod/v4"
import { StampMissionId } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { UpdatedAt } from "./system"
import {
  MissionKindEditUserIcon,
  MissionKindFollowUsers,
  MissionKindJoinSpace,
  MissionKindLoginDays,
  MissionKindLoginInTimeRange,
  MissionKindSetSuperCallUser,
  MissionKindShareFriendLink,
  MissionKindSnsLink,
  MissionKindTalkMinutesWithNUsers,
  MissionKindWatchLiveMinutes,
  StampMissionCompleteStatus,
  StampMissionKind,
  completeStatusFromLegacyCompleted,
} from "./stamp-mission"
import { assertNever } from "../lib/assert-never"

const UserStampMissionProgressBase = z.strictObject({
  user_id: z.string().describe("ユーザーID（コレクショングループクエリ対応のため。パスのIDはクエリ条件に使用できない制約がある）"),
  card_id: z.string().describe("親ドキュメントIDの冗長保持（コレクショングループクエリ対応のため。パスのIDはクエリ条件に使用できない制約がある）"),
})

// 限定ミッション - Boolean型
const UserStampMissionProgressLimitedBooleanMeta = UserStampMissionProgressBase.extend({
  kind: z.union([MissionKindEditUserIcon, MissionKindSetSuperCallUser, MissionKindSnsLink, MissionKindShareFriendLink]),
  complete_status: StampMissionCompleteStatus.optional().describe("ミッション達成ステータス（limitedミッション専用）。古いデータには存在しない"),
  completed: z.boolean().describe("【後方互換】ミッション完了フラグ（limitedミッション専用）。Flutter クライアント向けに二重書きしている"),
  completed_at: z.date().optional().describe("完了日時（JST、limitedミッション専用）"),
})

// 限定ミッション - Counter型（login_daysを除く）
const UserStampMissionProgressLimitedCounterMeta = UserStampMissionProgressBase.extend({
  kind: z.union([MissionKindFollowUsers, MissionKindTalkMinutesWithNUsers]),
  complete_status: StampMissionCompleteStatus.optional().describe("ミッション達成ステータス（limitedミッション専用）。古いデータには存在しない"),
  completed: z.boolean().describe("【後方互換】ミッション完了フラグ（limitedミッション専用）。Flutter クライアント向けに二重書きしている"),
  completed_at: z.date().optional().describe("完了日時（JST、limitedミッション専用）"),
  progress_count: z.number().describe("counter型の現在達成回数（limitedミッション専用）"),
})
// 限定ミッション - Counter型のlogin_days ミッション（last_progress_day_key を持つ）
const UserStampMissionProgressLimitedCounterLoginDaysMeta = UserStampMissionProgressLimitedCounterMeta.extend({
  kind: MissionKindLoginDays,
  last_progress_day_key: z.string().optional().describe("最後に進捗を更新した日のキー（YYYY-MM-DD形式、冪等性保証用。login_days専用）"),
})

// デイリーミッション - Boolean型
const UserStampMissionProgressDailyBooleanMeta = UserStampMissionProgressBase.extend({
  kind: z.union([MissionKindLoginInTimeRange, MissionKindJoinSpace]),
  complete_status: StampMissionCompleteStatus.optional().describe("ミッション達成ステータス。古いデータには存在しない"),
  completed: z.boolean().optional().describe("【後方互換】ミッション完了フラグ。Flutter クライアント向けに二重書きしている"),
})

// デイリーミッション - Counter型
const UserStampMissionProgressDailyCounterMeta = UserStampMissionProgressBase.extend({
  kind: z.union([MissionKindWatchLiveMinutes]),
  complete_status: StampMissionCompleteStatus.optional().describe("ミッション達成ステータス。古いデータには存在しない"),
  completed: z.boolean().describe("【後方互換】ミッション完了フラグ。Flutter クライアント向けに二重書きしている"),
  completed_at: z.date().optional().describe("完了日時（JST）"),
  progress_count: z.number().describe("counter型の現在達成回数"),
})

export const UserStampMissionProgressDocumentMeta = z.union([
  UserStampMissionProgressLimitedBooleanMeta,
  UserStampMissionProgressLimitedCounterLoginDaysMeta,
  UserStampMissionProgressLimitedCounterMeta,
  UserStampMissionProgressDailyBooleanMeta,
  UserStampMissionProgressDailyCounterMeta,
])

export type UserStampMissionProgressDocumentMeta = z.infer<typeof UserStampMissionProgressDocumentMeta>

export const UserStampMissionProgressDocumentBody = z.intersection(
  UserStampMissionProgressDocumentMeta,
  z.object({
    updated_at: UpdatedAt,
  })
)

export type UserStampMissionProgressDocumentBody = z.infer<typeof UserStampMissionProgressDocumentBody>

export type UserStampMissionProgressDocument = UserStampMissionProgressDocumentBody & {
  id: StampMissionId,
}

export class UserStampMissionProgressDocuments {

  static toDocumentData(doc: UserStampMissionProgressDocumentBody): object {
    return {
      ...doc,
    }
  }

  /**
   * Firestore ドキュメントから UserStampMissionProgressDocument を復元する。
   * 既存ドキュメントには complete_status がない場合があるため、
   * completed: boolean から complete_status へフォールバック変換する。
   */
  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): UserStampMissionProgressDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    const base = {
      id: StampMissionId(doc.id),
      user_id: data["user_id"],
      card_id: data["card_id"],
      updated_at: data["updated_at"],
    }

    const kind = data["kind"] as StampMissionKind
    const resolveCompleteStatus = (): StampMissionCompleteStatus => {
      if (data["complete_status"] !== undefined) return data["complete_status"] as StampMissionCompleteStatus
      if (data["completed"] !== undefined) return completeStatusFromLegacyCompleted(data["completed"] as boolean)
      return "in-progress"
    }

    switch (kind) {
      // Daily Boolean
      case "login_in_time_range":
      case "join_space": {
        const completeStatus = resolveCompleteStatus()
        return {
          ...base,
          kind,
          complete_status: completeStatus,
          completed: completeStatus !== "in-progress",
        } satisfies UserStampMissionProgressDocument
      }

      // Daily Counter
      case "watch_live_minutes": {
        const completeStatus = resolveCompleteStatus()
        return {
          ...base,
          kind,
          complete_status: completeStatus,
          completed: completeStatus !== "in-progress",
          completed_at: data["completed_at"],
          progress_count: data["progress_count"],
        } satisfies UserStampMissionProgressDocument
      }

      // Limited Boolean
      case "edit_user_icon":
      case "set_super_call_user":
      case "sns_link":
      case "share_friend_link": {
        const completeStatus = resolveCompleteStatus()
        return {
          ...base,
          kind,
          complete_status: completeStatus,
          completed: completeStatus !== "in-progress",
          completed_at: data["completed_at"],
        } satisfies UserStampMissionProgressDocument
      }

      // Limited Counter - login_days（特別なフィールドlast_progress_day_keyを持つ）
      case "login_days": {
        const completeStatus = resolveCompleteStatus()
        return {
          ...base,
          kind,
          complete_status: completeStatus,
          completed: completeStatus !== "in-progress",
          completed_at: data["completed_at"],
          progress_count: data["progress_count"],
          last_progress_day_key: data["last_progress_day_key"],
        } satisfies UserStampMissionProgressDocument
      }
      // Limited Counter
      case "follow_users":
      case "talk_minutes_with_n_users": {
        const completeStatus = resolveCompleteStatus()
        return {
          ...base,
          kind,
          complete_status: completeStatus,
          completed: completeStatus !== "in-progress",
          completed_at: data["completed_at"],
          progress_count: data["progress_count"],
        } satisfies UserStampMissionProgressDocument
      }

      default:
        assertNever(kind)
    }
  }
}
